import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom'
import {
	Hash,
	Clock,
	ArrowLeft,
	ArrowLeftRight,
	Cpu,
	Database,
	Fuel,
	Wallet,
	Box
} from 'lucide-react'

import { StatsCard } from '@/components/shared/StatsCard'
import { formatUnixTimestamp, formatBalanceInSUI, formatBalanceInMIST } from '@/utils/format';

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from '@/components/ui/pagination'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { useCheckpoint, useCheckpointTransactionBlocks } from '@/hooks/useApi'

const PAGINATION_ITEM_COUNT = 5;
const PAGE_SIZE = 10;

const clamp = (num: number, min: number, max: number) => {
	return Math.min(Math.max(num, min), max)
}

interface TransactionBlocksProps { id: string; totalPages: number }
function TransactionBlocks(props: TransactionBlocksProps) {
	const { id, totalPages } = props;
	if (!id) {
		return <> Not Found </>;
	}

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [startAtPage, setStartAtPage] = useState<number>(1);
	const [offset, setOffset] = useState<number>(0);

	const { data: checkpointTransactionBlocks } = useCheckpointTransactionBlocks(id, PAGE_SIZE, offset);

	const pageRange = useMemo(() => {
		if (!totalPages) return null;

		// if the page count is less than twice the PAGINATION_ITEM_COUNT (5), just list them all
		if (totalPages < (PAGINATION_ITEM_COUNT * 2)) {
			return [...Array(totalPages).keys()].map((i: number) => i + 1)
		}

		// if the page count is less than twice the PAGINATION_ITEM_COUNT (5), special render
		if (totalPages === (PAGINATION_ITEM_COUNT * 2)) {
			return [...Array(PAGINATION_ITEM_COUNT).keys()].map((i: number) => i + startAtPage)
		}

		// remainder render
		if ((totalPages % PAGINATION_ITEM_COUNT) && startAtPage === totalPages - (totalPages % PAGINATION_ITEM_COUNT) + 1) {
			return [...Array(totalPages % PAGINATION_ITEM_COUNT).keys()].map((i: number) => i + startAtPage)
		}

		return [...Array(PAGINATION_ITEM_COUNT).keys()].map((i: number) => i + startAtPage)

	}, [currentPage, startAtPage, PAGINATION_ITEM_COUNT, totalPages])

	if (!checkpointTransactionBlocks) {
		return <>Loading...</>
	}

	return (
		<>
			<Pagination className='mb-2'>
				<PaginationContent>

					{/* Previous Button */}
					{totalPages >= PAGINATION_ITEM_COUNT * 2 && <PaginationItem>
						<PaginationPrevious className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
							content={<span className='text-xs'>Prev</span> as any}
							onClick={() => {
								// determine if we need to adjust the page range (paging backward)
								if ((currentPage - 1) % PAGINATION_ITEM_COUNT === 0) {
									setStartAtPage(clamp(currentPage - PAGINATION_ITEM_COUNT, 1, totalPages))
								}
								setCurrentPage(clamp(currentPage - 1, 1, totalPages))
								setOffset(clamp(currentPage - 2, 0, totalPages - 1) * PAGE_SIZE)
							}}
						/>
					</PaginationItem>}

					{/* First Page Button */}
					{totalPages > PAGINATION_ITEM_COUNT * 2 && startAtPage !== 1 && <>
						<PaginationItem
							key={1}
						>
							<PaginationLink
								className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
								isActive={1 === currentPage}
								onClick={() => {
									setCurrentPage(1);
									setStartAtPage(1);
								}}
							><span className='text-xs'>{1}</span></PaginationLink>
						</PaginationItem>
						<PaginationItem>
							<PaginationEllipsis />
						</PaginationItem>
					</>}
					{/* Page Buttons */}
					{pageRange && pageRange.map((page: number) => (
						<PaginationItem
							key={page}
						>
							<PaginationLink
								className={`hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md ${page === currentPage ? 'bg-[#C0E6FF]/10 backdrop-blur-md' : ''}`}
								onClick={() => {
									setCurrentPage(clamp(page, 1, totalPages))
									setOffset((page - 1) * PAGE_SIZE)
								}}
							><span className='text-xs'>{page}</span></PaginationLink>
						</PaginationItem>
					))}

					{/* Last Page Button + Separator */}
					{totalPages >= PAGINATION_ITEM_COUNT * 2 && startAtPage <= (totalPages - PAGINATION_ITEM_COUNT) && <>
						<PaginationItem>
							<PaginationEllipsis />
						</PaginationItem>
						<PaginationItem
							key={totalPages}
						>
							<PaginationLink className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
								isActive={totalPages === currentPage}
								onClick={() => {
									setCurrentPage(totalPages);
									setStartAtPage(totalPages - PAGINATION_ITEM_COUNT + 1);
								}}
							><span className='text-xs'>{totalPages}</span></PaginationLink>
						</PaginationItem>
					</>}

					{/* Next Button */}
					{totalPages >= PAGINATION_ITEM_COUNT * 2 && <PaginationItem>
						<PaginationNext className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
							content={<span className='text-xs'>Prev</span> as any}
							onClick={() => {
								if (totalPages === currentPage) return;

								// determine if we need to adjust the page range (paging forward)
								if (currentPage % PAGINATION_ITEM_COUNT === 0) {
									setStartAtPage(clamp(currentPage + 1, 1, totalPages));
								}
								setCurrentPage(clamp(currentPage + 1, 1, totalPages));
								setOffset(clamp(currentPage, 0, totalPages - 1) * PAGE_SIZE)
							}}
						/>
					</PaginationItem>}
				</PaginationContent>
			</Pagination>
			<div className="rounded-lg border">
				<Table>
					<TableHeader className='bg-[#C0E6FF]/10 shadow-lg'>
						<TableRow className='pointer-events-none'>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Checkpoint</div>
								</div>
							</TableHead>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Digest</div>
								</div>
							</TableHead>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Timestamp</div>
								</div>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{checkpointTransactionBlocks?.map((transactionBlock: any) => (
							<TableRow key={transactionBlock.digest} className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'>
								<TableCell className='text-xs'>{Number(transactionBlock.checkpoint).toLocaleString()}</TableCell>
								<TableCell className='text-xs'>
									<Link to={`/transaction-blocks/${transactionBlock.digest}`} className="flex items-center space-x-2 text-[#4DA2FF] hover:text-[#C0E6FF]">
										<span className='hidden md:block'>{`${transactionBlock.digest.slice(0, 6)}...${transactionBlock.digest.slice(-6)}`}</span>
										<span className='md:hidden'>{`${transactionBlock.digest.slice(0, 6)}...`}</span>
									</Link>
								</TableCell>
								<TableCell>{formatUnixTimestamp(Number(transactionBlock.timestampMS))}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</>
	)
}

export function CheckpointDetailsPage() {
	const { id } = useParams<{ id: string }>();
	if (!id) {
		return <> Not Found </>
	}

	const { data: checkpointData } = useCheckpoint(id);

	const totalGasCost = useMemo(() => {
		if (!checkpointData) return 0;

		return (
			Number(checkpointData?.gasUsage.computationCost) +
			Number(checkpointData?.gasUsage.storageCost) -
			Number(checkpointData?.gasUsage.storageRebate)
		);
	}, [checkpointData])

	const totalPages = useMemo(() => {
		if (checkpointData) {
			return Math.ceil(checkpointData?.numberOfTransactions / PAGE_SIZE);
		}
		return 0;
	}, [checkpointData, PAGE_SIZE])

	if (!checkpointData) {
		return <> Loading... </>;
	}

	return (
		<div className="flex flex-col overflow-hidden w-full h-full">
			<div className='flex-1 overflow-y-auto overflow-x-hidden flex max-w-full 
  			[&::-webkit-scrollbar-track]:bg-gray-100
  			[&::-webkit-scrollbar-thumb]:bg-gray-300
  			dark:[&::-webkit-scrollbar-track]:bg-neutral-700
  			dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500'
			>
				<div className="flex flex-col w-full h-full gap-6 px-2 lg:px-4 pt-4">
					<h3 className="scroll-m-20 text-md lg:text-2xl font-bold tracking-tight">
						Checkpoint Details
					</h3>
					<div className="flex flex-row gap-6 min-w-0">
						<StatsCard
							title="Digest"
							className='text-sm flex-1'
							valueStyleOverride='text-lg lg:text-2xl overflow-hidden whitespace-nowrap text-ellipsis'
							value={checkpointData?.digest}
							icon={<Hash className='w-4 h-4' />}
						/>
					</div>
					<div className='grid grid-cols-1 lg:grid-cols-5 gap-2'>
						{/* Metadata */}
						<div className='lg:col-span-2'>
							<div className='flex flex-col gap-6'>
								<StatsCard
									title="Sequence Number"
									className='text-sm'
									valueStyleOverride='text-md xl:text-xl'
									value={Number(checkpointData?.sequenceNumber).toLocaleString()}
									icon={<Box className='h-4 w-4'/>}
								/>
								<StatsCard
									title="Timestamp"
									className='text-sm'
									valueStyleOverride='text-md xl:text-xl'
									value={checkpointData.timestampMS ? formatUnixTimestamp(Number(checkpointData.timestampMS)) : "--"}
									icon={<Clock className='h-4 w-4'/>}
								/>
								<StatsCard
									title="Previous Digest"
									className='text-sm'
									valueStyleOverride='text-md xl:text-xl'
									value={
										<Link to={`/checkpoints/${checkpointData.previousDigest}`} className="flex items-center space-x-2 text-[#4DA2FF] hover:text-[#C0E6FF]">
											{checkpointData.previousDigest.slice(0, 8)}...{checkpointData.previousDigest.slice(-6)}
										</Link>
									}
									icon={<ArrowLeft className='h-4 w-4' />}
								/>
							</div>
						</div>

						{/* Gas Usage */}
						<div className='lg:col-span-3'>
							<Card className='bg-transparent'>
								<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
									<div className='flex flex-row items-center px-2 pt-2'>
										<div className="rounded-full px-2 transition-colors">
											<Fuel className='h-4 w-4' />
										</div>
										<div className='flex-1'>
											<CardTitle className="text-sm md:text-md lg:text-lg font-medium">Gas Usage</CardTitle>
										</div>
									</div>
									<div className='px-4'>
										<CardDescription className='text-xs lg:text-md'>
											Gas cost breakdown for this transaction
										</CardDescription>
									</div>
								</CardHeader>
								<CardContent className='pt-4'>
									<div className='flex flex-col gap-3'>
										<div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
											<StatsCard
												className='text-sm'
												valueStyleOverride='text-md xl:text-xl'
												title="Total Cost"
												value={
													<>
														<div className='text-md'>
															{totalGasCost ? formatBalanceInSUI(Number(totalGasCost)) : '--'} SUI
														</div>
														<div className='text-sm text-muted-foreground'>
															{totalGasCost ? formatBalanceInMIST(Number(totalGasCost)) : '--'} MIST
														</div>
													</>
												}
												icon={<Wallet className='h-5 w-5' />}
											/>
											<StatsCard
												title="Computation Cost"
												className='text-sm'
												valueStyleOverride='text-md xl:text-xl'
												value={
													<>
														<div className='text-md'>
															{checkpointData?.gasUsage?.computationCost ? formatBalanceInSUI(Number(checkpointData?.gasUsage?.computationCost)) : '--'} SUI
														</div>
														<div className='text-sm text-muted-foreground'>
															{checkpointData?.gasUsage?.computationCost ? formatBalanceInMIST(Number(checkpointData?.gasUsage?.computationCost)) : '--'} MIST
														</div>
													</>
												}
												icon={<Cpu className='h-5 w-5' />}
											/>
										</div>
										<div className='grid grid-cols-1 md:grid-cols-3 gap-2'>
											<StatsCard
												title="Storage Cost"
												className='text-sm'
												valueStyleOverride='text-md xl:text-xl'
												value={
													<>
														<div className='text-md'>
															{checkpointData?.gasUsage?.storageCost ? formatBalanceInSUI(Number(checkpointData?.gasUsage?.storageCost)) : '--'} SUI
														</div>
														<div className='text-sm text-muted-foreground'>
															{checkpointData?.gasUsage?.storageCost ? formatBalanceInMIST(Number(checkpointData?.gasUsage?.storageCost)) : '--'} MIST
														</div>
													</>
												}
												icon={<Database className='h-5 w-5' />}
											/>
											<StatsCard
												title="Storage Rebate"
												className='text-sm'
												valueStyleOverride='text-md xl:text-xl'
												value={
													<>
														<div className='text-md'>
															{checkpointData?.gasUsage?.storageRebate ? formatBalanceInSUI(Number(checkpointData?.gasUsage?.storageRebate)) : '--'} SUI
														</div>
														<div className='text-sm text-muted-foreground'>
															{checkpointData?.gasUsage?.storageRebate ? formatBalanceInMIST(Number(checkpointData?.gasUsage?.storageRebate)) : '--'} MIST
														</div>
													</>
												}
												icon={<Database className='h-5 w-5' />}
											/>
											<StatsCard
												title="Non-Refundable Fee"
												className='text-sm'
												valueStyleOverride='text-md xl:text-xl'
												value={
													<>
														<div className='text-md'>
															{checkpointData?.gasUsage?.nonRefundableStorageFee ? formatBalanceInSUI(Number(checkpointData?.gasUsage?.nonRefundableStorageFee)) : '--'} SUI
														</div>
														<div className='text-sm text-muted-foreground'>
															{checkpointData?.gasUsage?.nonRefundableStorageFee ? formatBalanceInMIST(Number(checkpointData?.gasUsage?.nonRefundableStorageFee)) : '--'} MIST
														</div>
													</>
												}
												icon={<Database className='h-5 w-5' />}
											/>
										</div>
									</div>
								</CardContent>
							</Card>
						</div>
					</div>
					<Card className='bg-transparent'>
						<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
							<div className='flex flex-row items-center px-2 pt-2'>
								<div className="rounded-full px-2 transition-colors">
									<ArrowLeftRight className='h-5 w-5' />
								</div>
								<div className='flex-1'>
									<CardTitle className="text-xl font-medium">Transaction Blocks</CardTitle>
								</div>
							</div>
							<div className='px-4'>
								<CardDescription className='text-md'>
									Transaction blocks inside this checkpoint
								</CardDescription>
							</div>
						</CardHeader>
						<CardContent className='px-2'>
							<TransactionBlocks id={id} totalPages={totalPages} />
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	)
}